import {AxiosError} from "axios";
import {Dispatch, SetStateAction, useEffect, useState} from "react";
import Alert from "react-bootstrap/Alert";
import Button from "react-bootstrap/Button";
import FloatingLabel from "react-bootstrap/FloatingLabel";
import Form from "react-bootstrap/Form";
import InputGroup from "react-bootstrap/InputGroup";
import Modal from "react-bootstrap/Modal";
import {Trans, useTranslation} from "react-i18next";
import {useNavigate} from "react-router-dom";
import {ApiConfig} from "../api/portal/ApiConfig";
import {PDFApi} from "../api/portal/generated";
import phoneSetupIcon from '../assets/images/phone-setup.png'
import {CopyToClipboard} from "../common/CopyToClipboardButton";
import {DialogCloseCause} from "../common/Enums";
import {RenderAlert} from "../common/RenderAlert";
import {useSpinnerStatusContext} from "../hook/UseSpinnerStatusContext";
import {logger} from "../utils/Logger";
import {StringUtils} from "../utils/StringUtils";
import {Utils} from "../utils/Utils";

/**
 * The owner should handle the OnClose event. The owner is responsible for closing the dialog.
 */
type OnClose = (closeCause: DialogCloseCause) => void;

interface PDFAuthenticatorSetupWizardProps {
    onClose: OnClose
}

/*
 * REST api for accessing the back-end
 */
const pdfApi = new PDFApi(ApiConfig.config());

interface WizardPage {
    id: "intro" | "download" | "qrcode" | "validate" | "finish"
    title: string
}

/**
 * Intro wizard page component
 */
const Intro = () => {
    /**
     * i18next translation hook
     */
    const {t} = useTranslation('components');

    return (
        <div>
            <p>{t('pdf-authenticator-setup-wizard.intro-extra')}</p>
            <p>{t('pdf-authenticator-setup-wizard.intro-content')}</p>
            <ol>
                <li>{t('pdf-authenticator-setup-wizard.intro-step1')}</li>
                <li>{t('pdf-authenticator-setup-wizard.intro-step2')}</li>
                <li>{t('pdf-authenticator-setup-wizard.intro-step3')}</li>
            </ol>
        </div>
    )
}

/**
 * Download wizard page component
 */
const Download = () => {
    /**
     * i18next translation hook
     */
    const {t} = useTranslation('components');

    return (
        <div>
            <p>{t('pdf-authenticator-setup-wizard.download-content')}</p>
            <p>
                <Trans components={{
                    anchor: <a href="https://play.google.com/store/apps/details?id=com.ciphermail.ciphermail_pdf_qr"
                               target='_blank' rel='noreferrer'></a>
                }}>
                    {t('pdf-authenticator-setup-wizard.download-link-android')}
                </Trans>
            </p>
            <p>
                <Trans components={{anchor: <a href="https://apps.apple.com/us/app/ciphermail-pdf-authenticator/id1673495514"></a>}}>
                    {t('pdf-authenticator-setup-wizard.download-link-ios')}
                </Trans>
            </p>
            <p>{t('pdf-authenticator-setup-wizard.download-continue')}</p>
        </div>
    )
}

interface QRCodeProps {
    qrCode: string | undefined
    secretKey: string | undefined
}

/**
 * QR-Code wizard page component
 */
const QRCode = ({qrCode, secretKey}: QRCodeProps) =>
{
    /**
     * Set to true when a value is copied to the clipboard
     */
    const [copied, setCopied] = useState<boolean>(false);

    const {t} = useTranslation('components');

    return (
        <div>
            <p>{t('pdf-authenticator-setup-wizard.scan-qr-code-text1')}
                <img src={phoneSetupIcon} height={20} alt="Phone setup icon"/>
                {t('pdf-authenticator-setup-wizard.scan-qr-code-text2')}</p>
            <img src={`data:image/png;base64,${qrCode}`} alt="QR-code"/>
            <p>{t('pdf-authenticator-setup-wizard.scan-qr-code-text3')}</p>

            <p>{t('pdf-authenticator-setup-wizard.secret-key-copy-paste-text')}</p>
            <p>
                <Form.Group className="mb-3">
                    <Form.Label className="text-secondary">{t('pdf-authenticator-setup-wizard.secret-key-label')}</Form.Label>
                    <InputGroup.Text className="border-0 d-inline">
                        <CopyToClipboard value={secretKey} setCopied={setCopied}/>
                    </InputGroup.Text>
                    <Form.Control
                        className="text-wrap"
                        as="textarea"
                        rows={2}
                        value={secretKey}
                        disabled={true}
                        readOnly={true}/>
                </Form.Group>
                {copied && <Alert className="text-break"
                                  variant={"success"}>{t('common.copied-to-clipboard')}</Alert>}
            </p>
        </div>
    )
}

interface ValidateProps {
    qrCode: string
    otpCode: string
    password: string
    setPassword: Dispatch<SetStateAction<string>>
}

/**
 * Validate wizard page component
 */
const Validate = ({qrCode, otpCode, password, setPassword}: ValidateProps) =>
{
    /**
     * Set to true when a value is copied to the clipboard
     */
    const [copied, setCopied] = useState<boolean>(false);

    /**
     * i18next translation hook
     */
    const {t} = useTranslation('components');

    return (
        <div>
            <p>{t('pdf-authenticator-setup-wizard.validate-intro')}</p>
            <img src={`data:image/png;base64,${qrCode}`} alt="OTP QR-code"/>
            <ol>
                <li>{t('pdf-authenticator-setup-wizard.validate-step1')}</li>
                <li>{t('pdf-authenticator-setup-wizard.validate-step2')}</li>
                <li>{t('pdf-authenticator-setup-wizard.validate-step3')}</li>
            </ol>

            <p>{t('pdf-authenticator-setup-wizard.otp-code-copy-paste-text')}</p>
            <p>
                <Form.Group className="mb-3">
                    <Form.Label className="text-secondary">{t('pdf-authenticator-setup-wizard.otp-code-label')}</Form.Label>
                    <InputGroup.Text className="border-0 d-inline">
                        <CopyToClipboard value={otpCode} setCopied={setCopied}/>
                    </InputGroup.Text>
                    <Form.Control
                        className="text-wrap"
                        as="textarea"
                        rows={2}
                        value={otpCode}
                        disabled={true}
                        readOnly={true}/>
                </Form.Group>
                {copied && <Alert className="text-break"
                                  variant={"success"}>{t('common.copied-to-clipboard')}</Alert>}
            </p>

            <FloatingLabel label={t('pdf-authenticator-setup-wizard.validate-label')} className="text-secondary">
                <Form.Control type="text"
                              value={password}
                              placeholder="Enter password generated by PDF Authenticator"
                              onChange={e => setPassword(e.target.value)}/>
            </FloatingLabel>
        </div>
    )
}

const Finish = () => {
    const {t} = useTranslation('components');

    return (
        <div>
            <p>{t('pdf-authenticator-setup-wizard.password-match')}</p>
            <p>{t('pdf-authenticator-setup-wizard.finish-text1')}</p>
            <p>{t('pdf-authenticator-setup-wizard.finish-text2')}</p>
            <p>{t('pdf-authenticator-setup-wizard.finish-text3')}</p>
        </div>
    )
}

export const PDFAuthenticatorSetupWizard = ({onClose}: PDFAuthenticatorSetupWizardProps) => {
    /**
     * Navigate is used to navigate to the login page if the user is no longer authenticated
     */
    const navigate = useNavigate();
    /**
     * The error message if some general error occurred
     */
    const [errorMessage, setErrorMessage] = useState<string>();
    /**
     * The current wizard index
     */
    const [wizardIndex, setWizardIndex] = useState<number>(0);
    /**
     * The base64 encoded QR-code containing the OTP secret
     */
    const [otpSecretKeyQRCode, setOtpSecretKeyQRCode] = useState<string>("");
    /**
     * The OTP secret key
     */
    const [otpSecretKey, setOtpSecretKey] = useState<string>("");
    /**
     * Generated OTP QR code for the user which will be used to check whether the user correctly configured PDF Authenticator
     */
    const [otpQRCode, setOtpQRCode] = useState<string>("");
    /**
     * Generated OTP JSON code for the user
     */
    const [oTPJSONCode, setOTPJSONCode] = useState<string>("");
    /**
     * Generated password for the user which will be used to check whether the user correctly configured PDF Authenticator
     */
    const [generatedPassword, setGeneratedPassword] = useState<string>("");
    /**
     * User provided password which should match the generated password
     */
    const [password, setPassword] = useState<string>("");
    /**
     * Keeps track of activities for which the spinner should be shown. If an activity is started, the counter should
     * be increased by one and after the activity
     */
    const {setSpinnerStateCounter} = useSpinnerStatusContext();
    /**
     * i18next translation hook
     */
    const {t} = useTranslation('components');

    // Constant values used for generating the validation QR code
    const PasswordID = "CipherMail";
    const PasswordLength = 6;

    /**
     * The Wizard pages
     */
    const [wizardPages] = useState<WizardPage[]>(
        [
            {id: "intro", title: t('pdf-authenticator-setup-wizard.intro-title')},
            {id: "download", title: t('pdf-authenticator-setup-wizard.download-title')},
            {id: "qrcode", title: t('pdf-authenticator-setup-wizard.scan-qr-code-title')},
            {id: "validate", title: t('pdf-authenticator-setup-wizard.validate-qr-code-title')},
            {id: "finish", title: t('pdf-authenticator-setup-wizard.finish-title')},
        ]
    );

    useEffect(() => {
        if (setSpinnerStateCounter !== undefined) {
            setSpinnerStateCounter(count => count + 1);
        }

        pdfApi.pDFControllerGetOTPSecretKeyQRCode()
            .then(response => {
                setOtpSecretKeyQRCode(response.data);
            })
            .catch((err: Error | AxiosError) => {
                logger.error("Error getting OTP Secret Key QR Code", err);
                Utils.redirectToLoginIfNotAuthenticated(err, navigate);
                setErrorMessage(Utils.getErrorMessage(err, t));
            })
            .finally(() => {
                if (setSpinnerStateCounter !== undefined) {
                    setSpinnerStateCounter(count => count - 1);
                }
            })
    }, [navigate,
        setSpinnerStateCounter,
        t
    ]);

    useEffect(() => {
        if (setSpinnerStateCounter !== undefined) {
            setSpinnerStateCounter(count => count + 1);
        }

        pdfApi.pDFControllerGetOTPSecretKey()
            .then(response => {
                setOtpSecretKey(response.data);
            })
            .catch((err: Error | AxiosError) => {
                logger.error("Error getting OTP Secret Key", err);
                Utils.redirectToLoginIfNotAuthenticated(err, navigate);
                setErrorMessage(Utils.getErrorMessage(err, t));
            })
            .finally(() => {
                if (setSpinnerStateCounter !== undefined) {
                    setSpinnerStateCounter(count => count - 1);
                }
            })
    }, [navigate,
        setSpinnerStateCounter,
        t
    ]);

    useEffect(() => {
        if (setSpinnerStateCounter !== undefined) {
            setSpinnerStateCounter(count => count + 1);
        }

        pdfApi.pDFControllerGetOTPQRCode(PasswordID, PasswordLength)
            .then(response => {
                setOtpQRCode(response.data);
            })
            .catch((err: Error | AxiosError) => {
                logger.error("Error getting OTP QR Code", err);
                Utils.redirectToLoginIfNotAuthenticated(err, navigate);
                setErrorMessage(Utils.getErrorMessage(err, t));
            })
            .finally(() => {
                if (setSpinnerStateCounter !== undefined) {
                    setSpinnerStateCounter(count => count - 1);
                }
            })
    }, [navigate,
        setSpinnerStateCounter,
        t
    ]);

    useEffect(() => {
        if (setSpinnerStateCounter !== undefined) {
            setSpinnerStateCounter(count => count + 1);
        }

        pdfApi.pDFControllerGetOTPJSONCode(PasswordID, PasswordLength)
            .then(response => {
                setOTPJSONCode(response.data);
            })
            .catch((err: Error | AxiosError) => {
                logger.error("Error getting OTP JSON Code", err);
                Utils.redirectToLoginIfNotAuthenticated(err, navigate);
                setErrorMessage(Utils.getErrorMessage(err, t));
            })
            .finally(() => {
                if (setSpinnerStateCounter !== undefined) {
                    setSpinnerStateCounter(count => count - 1);
                }
            })
    }, [navigate,
        setSpinnerStateCounter,
        t
    ]);

    useEffect(() => {
        if (StringUtils.isBlank(oTPJSONCode)) {
            return;
        }

        if (setSpinnerStateCounter !== undefined) {
            setSpinnerStateCounter(count => count + 1);
        }

        pdfApi.pDFControllerGetPdfPassword(oTPJSONCode)
            .then(response => {
                setGeneratedPassword(response.data);
            })
            .catch((err: Error | AxiosError) => {
                logger.error("Error getting password", err);
                Utils.redirectToLoginIfNotAuthenticated(err, navigate);
                setErrorMessage(Utils.getErrorMessage(err, t));
            })
            .finally(() => {
                if (setSpinnerStateCounter !== undefined) {
                    setSpinnerStateCounter(count => count - 1);
                }
            })
    }, [navigate,
        setSpinnerStateCounter,
        oTPJSONCode,
        t
    ]);

    // called when the dialog is closed without clicking download
    const onHideDialog = () => {
        onClose(DialogCloseCause.Cancel);
    }

    const currentPage = () => {
        return wizardPages[wizardIndex];
    }

    const isLastPage = () => {
        return wizardIndex === (wizardPages.length - 1)
    }

    const isFirstPage = () => {
        return wizardIndex === 0
    }

    const nextPage = () => {
        if (isLastPage()) {
            throw Error("Last page reached")
        }

        setWizardIndex(index => index + 1)
    }

    const previousPage = () => {
        if (isFirstPage()) {
            throw Error("At first page")
        }

        setWizardIndex(index => index - 1)
    }

    const cancel = () => {
        onClose(DialogCloseCause.Cancel);
    }

    const finish = () => {
         onClose(DialogCloseCause.Succes);
    }

    const isNextDisabled = () => {
        if (isLastPage() || StringUtils.isBlank(otpSecretKeyQRCode) || StringUtils.isBlank(generatedPassword)) {
            return true;
        }

        return currentPage().id === "validate" && StringUtils.isBlank(password);
    }

    const onClickNext = () => {
        if (currentPage().id === "validate" && generatedPassword !== password) {
            setErrorMessage(t('pdf-authenticator-setup-wizard.password-mismatch'))

            return;
        }

        setErrorMessage(undefined);

        nextPage();
    }

    return (
        <Modal show={true} onHide={() => onHideDialog()} size="xl" fullscreen="lg-down" scrollable={false}>
            <Modal.Header closeButton>
                <Modal.Title>{currentPage().title}</Modal.Title>
            </Modal.Header>
            <div className="row flex-grow-1">
                <main className="col d-flex flex-column overflow-auto vh-80">
                    <Modal.Body>
                        <RenderAlert message={errorMessage} setMessage={setErrorMessage}
                                     tooltipPlacement={"bottom"}/>
                        <Form>
                            {currentPage().id === "intro" && <Intro/>}
                            {currentPage().id === "download" && <Download/>}
                            {currentPage().id === "qrcode" && <QRCode qrCode={otpSecretKeyQRCode} secretKey={otpSecretKey}/>}
                            {currentPage().id === "validate" &&
                                <Validate qrCode={otpQRCode} otpCode={oTPJSONCode} password={password} setPassword={setPassword}/>}
                            {currentPage().id === "finish" && <Finish/>}
                        </Form>
                    </Modal.Body>
                    <Modal.Footer>
                        <Button variant="primary" disabled={isFirstPage()} onClick={previousPage}>
                            {t('wizard.previous-button')}
                        </Button>
                        <Button variant="primary" disabled={isNextDisabled()} onClick={onClickNext}>
                            {t('wizard.next-button')}
                        </Button>
                        <Button variant="danger" onClick={cancel}>
                            {t('wizard.cancel-button')}
                        </Button>
                        <Button variant="success" disabled={!isLastPage() } onClick={finish}>
                            {t('wizard.finish-button')}
                        </Button>
                    </Modal.Footer>
                </main>
            </div>
        </Modal>
    )
}
